import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, pollJob, API } from "@/lib/api";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { DeleteClientButton } from "@/components/DeleteClientButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import TradelinesPanel from "@/components/TradelinesPanel";
import ClientDocuments from "@/components/ClientDocuments";
import ClientMonitoring from "@/components/ClientMonitoring";
import ClientAgreements from "@/components/ClientAgreements";
import GovernmentLetters from "@/components/GovernmentLetters";
import PrintUnlockPanel from "@/components/PrintUnlockPanel";
import { ArrowLeft, Plus, Sparkles, FileText, Copy, Trash2, Loader2, Car, Home, CreditCard, Banknote, Building2, Check, X, Upload, ScanLine, Send, Mail, Users, Pencil, RefreshCw, Save, ShieldAlert } from "lucide-react";

const BUREAUS = ["Equifax", "Experian", "TransUnion"];
const ICONS = { car: Car, home: Home, "credit-card": CreditCard, banknote: Banknote, "building-2": Building2 };

function downscaleImage(file, maxDim = 1600, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Invalid image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const { user } = useAuth();
  const isAgent = user?.role === "agent";

  const loadClient = useCallback(() => api.get(`/clients/${id}`).then((r) => setClient(r.data)).catch(() => {}), [id]);
  useEffect(() => { loadClient(); }, [loadClient]);

  if (!client) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/clients")} className="text-sm text-muted-foreground hover:text-white flex items-center gap-1 mb-4" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to clients
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold text-2xl">{client.name?.[0]?.toUpperCase()}</div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-none">{client.name}</h1>
            <p className="text-muted-foreground text-sm mt-1">{client.email || "No email"} · {client.phone || "No phone"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isAgent && <EditDetails client={client} onSaved={loadClient} />}
          {!isAgent && <AssignAgents client={client} onSaved={loadClient} />}
          <ScoreEditor client={client} onSaved={loadClient} />
          <DeleteClientButton client={client} variant="button" onDeleted={(status) => { if (status === "deleted") navigate("/clients"); }} />
        </div>
      </div>

      <Tabs defaultValue={isAgent ? "documents" : "disputes"}>
        <TabsList className="bg-card/60 border border-border mb-6 flex-wrap h-auto">
          {!isAgent && <TabsTrigger value="disputes" data-testid="tab-disputes">Negative Items & Disputes</TabsTrigger>}
          {!isAgent && <TabsTrigger value="gov" data-testid="tab-gov">Gov. Letters</TabsTrigger>}
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          {!isAgent && <TabsTrigger value="monitoring" data-testid="tab-monitoring">Monitoring</TabsTrigger>}
          {!isAgent && <TabsTrigger value="agreements" data-testid="tab-agreements">Agreements</TabsTrigger>}
          {!isAgent && <TabsTrigger value="qualify" data-testid="tab-qualify">Qualifications</TabsTrigger>}
          <TabsTrigger value="tradelines" data-testid="tab-tradelines">Tradelines</TabsTrigger>
        </TabsList>

        {!isAgent && <TabsContent value="disputes"><DisputesTab client={client} /></TabsContent>}
        {!isAgent && <TabsContent value="gov"><GovernmentLetters clientId={id} /></TabsContent>}
        <TabsContent value="documents"><ClientDocuments clientId={id} /></TabsContent>
        {!isAgent && <TabsContent value="monitoring"><ClientMonitoring clientId={id} /></TabsContent>}
        {!isAgent && <TabsContent value="agreements"><ClientAgreements clientId={id} /></TabsContent>}
        {!isAgent && <TabsContent value="qualify"><QualifyTab client={client} /></TabsContent>}
        <TabsContent value="tradelines"><TradelinesPanel clientId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}

function ScoreEditor({ client, onSaved }) {
  const [score, setScore] = useState(client.credit_score || "");
  const save = async () => {
    try {
      await api.put(`/clients/${client.id}`, { credit_score: parseInt(score) || 0 });
      toast.success("Credit score updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-4 bg-card/60 border-border flex items-end gap-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Current Credit Score</Label>
        <Input type="number" value={score} onChange={(e) => setScore(e.target.value)} className="w-28 font-mono text-lg" data-testid="edit-score-input" />
      </div>
      <Button onClick={save} className="rounded-lg" data-testid="save-score-btn">Update</Button>
    </Card>
  );
}

function EditField({ label, v, set, placeholder, maxLength, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={v} onChange={(e) => set(e.target.value)} placeholder={placeholder} maxLength={maxLength} data-testid={testid} />
    </div>
  );
}

function EditDetails({ client, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({});
  const openDlg = () => {
    setF({ name: client.name || "", email: client.email || "", phone: client.phone || "", address: client.address || "", city: client.city || "", state: client.state || "", zip: client.zip || "", dob: client.dob || "", ssn_last4: client.ssn_last4 || "" });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, f);
      toast.success("Client details updated — new letters will use this info");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <>
      <Button variant="secondary" className="rounded-lg" onClick={openDlg} data-testid="edit-details-btn"><Pencil className="h-4 w-4 mr-1" /> Edit Details</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border max-h-[90vh] overflow-y-auto" description="Edit client contact and identity details used in dispute letters">
          <DialogHeader><DialogTitle className="font-display">Edit Client Details</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <EditField label="Full Name" v={f.name} set={(v) => setF({ ...f, name: v })} testid="edit-name" />
            <EditField label="Email" v={f.email} set={(v) => setF({ ...f, email: v })} testid="edit-email" />
            <EditField label="Phone" v={f.phone} set={(v) => setF({ ...f, phone: v })} testid="edit-phone" />
            <EditField label="Date of Birth" v={f.dob} set={(v) => setF({ ...f, dob: v })} placeholder="MM/DD/YYYY" testid="edit-dob" />
            <EditField label="SSN (last 4)" v={f.ssn_last4} set={(v) => setF({ ...f, ssn_last4: v })} maxLength={4} testid="edit-ssn" />
            <div className="sm:col-span-2"><EditField label="Street Address" v={f.address} set={(v) => setF({ ...f, address: v })} placeholder="123 Main St" testid="edit-address" /></div>
            <EditField label="City" v={f.city} set={(v) => setF({ ...f, city: v })} testid="edit-city" />
            <div className="grid grid-cols-2 gap-3">
              <EditField label="State" v={f.state} set={(v) => setF({ ...f, state: v })} placeholder="CA" maxLength={2} testid="edit-state" />
              <EditField label="ZIP" v={f.zip} set={(v) => setF({ ...f, zip: v })} placeholder="90001" maxLength={10} testid="edit-zip" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} className="rounded-lg" data-testid="save-details-btn">{saving ? "Saving…" : "Save Details"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DisputesTab({ client }) {
  const [items, setItems] = useState([]);
  const [letters, setLetters] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewLetter, setViewLetter] = useState(null);
  const [printUnlocked, setPrintUnlocked] = useState(false);
  const [form, setForm] = useState({ creditor: "", account_number: "", item_type: "Collection", amount: "", reason: "Not mine / Inaccurate", bureaus: [...BUREAUS], notes: "" });
  const [editContent, setEditContent] = useState("");
  const [savingLetter, setSavingLetter] = useState(false);
  const [regen, setRegen] = useState(false);
  const [regenAll, setRegenAll] = useState(false);
  const [movLoading, setMovLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  const load = useCallback(() => {
    api.get(`/clients/${client.id}/negative-items`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/clients/${client.id}/dispute-letters`).then((r) => setLetters(r.data)).catch(() => {});
  }, [client.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/letter-templates`).then((r) => setTemplates(r.data)).catch(() => {}); }, []);

  const addItem = async () => {
    if (!form.creditor.trim()) return toast.error("Creditor name required");
    setSaving(true);
    try {
      await api.post(`/clients/${client.id}/negative-items`, form);
      toast.success("Negative item added");
      setOpen(false);
      setForm({ creditor: "", account_number: "", item_type: "Collection", amount: "", reason: "Not mine / Inaccurate", bureaus: [...BUREAUS], notes: "" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const generate = async (item) => {
    setGenerating(item.id);
    try {
      const { data } = await api.post(`/clients/${client.id}/dispute-letters`, { item_id: item.id, bureaus: item.bureaus });
      await pollJob(data.job_id);
      toast.success(`Dispute letters generated for ${item.bureaus.length} bureau(s)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setGenerating(null); }
  };

  const onUploadReport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const dataUrl = await downscaleImage(file, 1600, 0.85);
      const { data } = await api.post(`/clients/${client.id}/analyze-report`, { image_base64: dataUrl });
      const result = await pollJob(data.job_id);
      toast.success(`AI found ${result.items_found} item(s) and drafted ${result.letters.length} bureau letter(s)`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const deleteItem = async (id) => { await api.delete(`/negative-items/${id}`); load(); };
  const copy = (text) => copyText(text, "Letter copied to clipboard");

  const openLetter = (l) => { setViewLetter(l); setEditContent(l.content || ""); };
  const saveLetter = async () => {
    setSavingLetter(true);
    try {
      const { data } = await api.put(`/dispute-letters/${viewLetter.id}`, { content: editContent });
      toast.success("Letter saved — the mailed copy will use this version");
      setViewLetter(data); setEditContent(data.content || ""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Save failed"); } finally { setSavingLetter(false); }
  };
  const regenLetter = async () => {
    setRegen(true);
    try {
      const { data } = await api.post(`/dispute-letters/${viewLetter.id}/regenerate`);
      const result = await pollJob(data.job_id);
      setEditContent(result.content || "");
      setViewLetter((v) => ({ ...v, content: result.content }));
      toast.success("Letter re-generated with AI");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setRegen(false); }
  };
  const regenAllLetters = async () => {
    setRegenAll(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/dispute-letters/regenerate-all`);
      const r = await pollJob(data.job_id);
      toast.success(`Re-generated ${r.regenerated} letter(s) with your latest details`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setRegenAll(false); }
  };
  const applyTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    const map = {
      "{client_name}": client?.name || "",
      "{address}": client?.address || "",
      "{city}": client?.city || "",
      "{state}": client?.state || "",
      "{zip}": client?.zip || "",
      "{ssn_last4}": client?.ssn_last4 || "",
      "{dob}": client?.dob || "",
      "{bureau}": viewLetter?.bureau || "",
      "{creditor}": viewLetter?.creditor || "",
      "{date}": new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
    };
    let body = t.body || "";
    Object.entries(map).forEach(([k, v]) => { body = body.split(k).join(v); });
    body = body
      .split("\n")
      .map((ln) => (ln.trim() === "," ? "" : ln.replace(/^\s*,\s*/, "")))
      .join("\n")
      .replace(/\n{3,}/g, "\n\n");
    setEditContent(body);
    toast.success(`Inserted template: ${t.name}`);
  };

  const genMov = async () => {
    setMovLoading(true);
    try {
      const { data } = await api.post(`/dispute-letters/${viewLetter.id}/mov`);
      const r = await pollJob(data.job_id);
      const res = await api.get(`/clients/${client.id}/dispute-letters`);
      setLetters(res.data);
      toast.success("MOV follow-up letter created");
      const nl = res.data.find((l) => l.id === r.letter_id);
      if (nl) openLetter(nl);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setMovLoading(false); }
  };

  const toggleBureau = (b) => setForm((f) => ({ ...f, bureaus: f.bureaus.includes(b) ? f.bureaus.filter((x) => x !== b) : [...f.bureaus, b] }));

  const consolidated = letters.filter((l) => l.consolidated && l.letter_kind !== "mov");
  const movLetters = letters.filter((l) => l.letter_kind === "mov");
  const pdfLocked = (l) => l?.consolidated && l?.letter_kind !== "mov" && !printUnlocked;

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-primary/15 via-card/60 to-transparent border-primary/30" data-testid="upload-report-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-lg bg-primary/20 flex items-center justify-center shrink-0"><ScanLine className="h-6 w-6 text-primary" /></div>
            <div>
              <h3 className="font-display text-xl font-semibold">Upload Credit Report</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">Drop a photo or screenshot of the credit report. Our AI reads every negative item, disputes it, and drafts all three bureau letters ready to send.</p>
            </div>
          </div>
          <label className="shrink-0">
            <input type="file" accept="image/*" className="hidden" onChange={onUploadReport} disabled={uploading} data-testid="upload-report-input" />
            <span className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploading ? "bg-secondary text-muted-foreground" : "bg-primary text-white hover:bg-primary/90"}`} data-testid="upload-report-btn">
              {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing report…</> : <><Upload className="h-4 w-4" /> Upload Report</>}
            </span>
          </label>
        </div>
      </Card>

      {consolidated.length > 0 && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h3 className="font-display text-xl font-semibold flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Ready-to-Send Bureau Letters</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" className="rounded-lg" onClick={regenAllLetters} disabled={regenAll} data-testid="regenerate-all-btn">{regenAll ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Re-generate All</Button>
            </div>
          </div>
          <PrintUnlockPanel clientId={client.id} onStatus={setPrintUnlocked} onChanged={load} />
          <div className="grid sm:grid-cols-3 gap-3">
            {consolidated.slice(0, 3).map((l) => (
              <Card key={l.id} className="p-4 bg-card/60 border-primary/30" data-testid={`consolidated-letter-${l.bureau}`}>
                <div className="text-xs uppercase tracking-widest text-primary font-bold">{l.bureau}</div>
                <div className="text-sm text-muted-foreground mt-1 line-clamp-3">{l.content?.slice(0, 140)}…</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="rounded-lg flex-1" onClick={() => openLetter(l)} data-testid={`view-consolidated-${l.bureau}`}><FileText className="h-3.5 w-3.5 mr-1" /> Open</Button>
                  <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => copyText(l.content, "Copied")} data-testid={`copy-consolidated-${l.bureau}`}><Copy className="h-3.5 w-3.5" /></Button>
                </div>
                {l.mail_status === "mailed" && <div className="text-[11px] text-emerald-400 mt-2" data-testid={`mailed-${l.bureau}`}>Mailed {l.mailed_at ? new Date(l.mailed_at).toLocaleDateString() : ""}</div>}
              </Card>
            ))}
          </div>
        </div>
      )}

      {movLetters.length > 0 && (
        <div>
          <h3 className="font-display text-xl font-semibold flex items-center gap-2 mb-3"><ShieldAlert className="h-4 w-4 text-amber-400" /> Method of Verification (MOV) Letters</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {movLetters.map((l) => (
              <Card key={l.id} className="p-4 bg-card/60 border-amber-500/30" data-testid={`mov-letter-${l.id}`}>
                <div className="text-xs uppercase tracking-widest text-amber-300 font-bold">{l.bureau} · MOV</div>
                <div className="text-sm text-muted-foreground mt-1 line-clamp-3">{l.content?.slice(0, 140)}…</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="rounded-lg flex-1" onClick={() => openLetter(l)} data-testid={`view-mov-${l.id}`}><FileText className="h-3.5 w-3.5 mr-1" /> Open</Button>
                  <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => copyText(l.content, "Copied")} data-testid={`copy-mov-${l.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold">Negative Items</h3>
        <Button onClick={() => setOpen(true)} className="rounded-lg" data-testid="add-negative-btn"><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
      </div>

      <div className="grid gap-3">
        {items.map((item) => {
          const itemLetters = letters.filter((l) => l.item_id === item.id && l.letter_kind !== "mov");
          return (
            <Card key={item.id} className="p-4 bg-card/60 border-border" data-testid={`negative-item-${item.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{item.creditor}</span>
                    <Badge variant="secondary" className="text-xs">{item.item_type}</Badge>
                    <Badge className={`text-xs ${item.status === "Disputed" ? "bg-blue-500/20 text-blue-300" : "bg-secondary text-muted-foreground"}`}>{item.status}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Acct {item.account_number || "N/A"} · {item.amount || "—"} · {item.reason}</div>
                  <div className="flex gap-1.5 mt-2">{item.bureaus.map((b) => <span key={b} className="text-[10px] px-2 py-0.5 rounded bg-secondary text-muted-foreground">{b}</span>)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" onClick={() => generate(item)} disabled={generating === item.id} className="rounded-lg" data-testid={`generate-letters-${item.id}`}>
                    {generating === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /> AI Letters</>}
                  </Button>
                  <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteItem(item.id)} data-testid={`delete-item-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              {itemLetters.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                  {itemLetters.map((l) => (
                    <button key={l.id} onClick={() => openLetter(l)} className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors" data-testid={`view-letter-${l.id}`}>
                      <FileText className="h-3.5 w-3.5" /> {l.bureau} letter
                    </button>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
        {!items.length && <p className="text-sm text-muted-foreground py-8 text-center">No negative items yet. Add the items you want to dispute.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/90 backdrop-blur-xl border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Add Negative Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Creditor / Furnisher *</Label><Input value={form.creditor} onChange={(e) => setForm({ ...form, creditor: e.target.value })} data-testid="neg-creditor" /></div>
              <div className="space-y-2"><Label>Account #</Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="neg-account" /></div>
              <div className="space-y-2">
                <Label>Type</Label>
                <select value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm" data-testid="neg-type">
                  {["Collection", "Medical Collection", "Late Payment", "Charge-off", "Hard Inquiry", "Bankruptcy", "Repossession", "Foreclosure", "Judgment", "Tax Lien", "Student Loan", "Eviction", "Identity Theft"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-2"><Label>Amount</Label><Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="$" data-testid="neg-amount" /></div>
            </div>
            <div className="space-y-2">
              <Label>Reason for Dispute</Label>
              <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm" data-testid="neg-reason">
                {["Not mine / Inaccurate", "Never late / Incorrect payment history", "Account paid / settled", "Duplicate account", "Unauthorized hard inquiry", "Identity theft / fraud", "Balance is incorrect", "Account closed by consumer", "Beyond reporting period"].map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Dispute with Bureaus</Label>
              <div className="flex gap-2">
                {BUREAUS.map((b) => (
                  <button key={b} type="button" onClick={() => toggleBureau(b)} className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${form.bureaus.includes(b) ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground"}`} data-testid={`bureau-toggle-${b}`}>{b}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2"><Label>Notes (optional)</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="neg-notes" /></div>
          </div>
          <DialogFooter><Button onClick={addItem} disabled={saving} className="rounded-lg" data-testid="save-negative-btn">{saving ? "Saving…" : "Add Item"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewLetter} onOpenChange={(o) => !o && setViewLetter(null)}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border max-w-2xl max-h-[90vh] overflow-y-auto" description="Review, edit, or re-generate the dispute letter before mailing">
          <DialogHeader><DialogTitle className="font-display flex items-center justify-between pr-6">
            <span>{viewLetter?.bureau} Dispute Letter</span>
            <Button size="sm" variant="secondary" onClick={() => copy(editContent)} data-testid="copy-letter-btn"><Copy className="h-4 w-4 mr-1" /> Copy</Button>
          </DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Edit any details (address, DOB, SSN last 4, wording) below, then Save. The printed letter uses your saved version.</p>
          {templates.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value=""
                onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); e.target.value = ""; }}
                className="h-9 rounded-lg bg-background/50 border border-border text-xs px-2 text-white"
                data-testid="insert-template-select"
              >
                <option value="">Insert a saved template…</option>
                {templates.map((t) => (<option key={t.id} value={t.id}>{t.name}{t.category ? ` · ${t.category}` : ""}</option>))}
              </select>
              <span className="text-[11px] text-muted-foreground">Replaces the editor text with the template (auto-filled).</span>
            </div>
          )}
          <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="font-mono text-xs leading-relaxed min-h-[360px] bg-background/50" data-testid="letter-content" />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={saveLetter} disabled={savingLetter || editContent === viewLetter?.content} className="rounded-lg" data-testid="save-letter-btn">
              {savingLetter ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />} Save Changes
            </Button>
            <Button size="sm" variant="secondary" onClick={regenLetter} disabled={regen} className="rounded-lg" data-testid="regenerate-letter-btn">
              {regen ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Re-generate with AI
            </Button>
            {viewLetter?.letter_kind !== "mov" && (
              <Button size="sm" variant="secondary" onClick={genMov} disabled={movLoading} className="rounded-lg" data-testid="mov-letter-btn">
                {movLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldAlert className="h-4 w-4 mr-1" />} MOV Follow-up
              </Button>
            )}
            {pdfLocked(viewLetter) ? (
              <Badge variant="secondary" className="text-muted-foreground" data-testid="pdf-locked-badge">PDF locked until client pays</Badge>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => window.open(`${API}/dispute-letters/${viewLetter.id}/render?fmt=pdf&auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}`, "_blank")} className="rounded-lg" data-testid="preview-pdf-btn"><FileText className="h-4 w-4 mr-1" /> Preview PDF</Button>
            )}
          </div>
          {viewLetter?.updated_by && (
            <p className="text-[11px] text-muted-foreground" data-testid="letter-edit-history">Last edited by {viewLetter.updated_by} · {new Date(viewLetter.updated_at).toLocaleString()}{viewLetter.edit_source ? ` · ${viewLetter.edit_source}` : ""}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignAgents({ client, onSaved }) {
  const [open, setOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [sel, setSel] = useState(client.agent_ids || []);
  useEffect(() => {
    if (open) {
      api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
      setSel(client.agent_ids || []);
    }
  }, [open, client]);
  const toggle = (aid) => setSel((s) => (s.includes(aid) ? s.filter((x) => x !== aid) : [...s, aid]));
  const save = async () => {
    try {
      await api.put(`/clients/${client.id}/agents`, { agent_ids: sel });
      toast.success("Agents updated");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <Button variant="secondary" className="rounded-lg" onClick={() => setOpen(true)} data-testid="assign-agents-btn"><Users className="h-4 w-4 mr-1" /> Assign Agents</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border">
          <DialogHeader><DialogTitle className="font-display">Assign Agents</DialogTitle></DialogHeader>
          {agents.length === 0 ? (
            <p className="text-sm text-muted-foreground">No agents yet. Create agents in the Team page.</p>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {agents.map((a) => (
                <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded hover:bg-secondary" data-testid={`assign-agent-${a.id}`}>
                  <input type="checkbox" checked={sel.includes(a.id)} onChange={() => toggle(a.id)} className="accent-primary" />
                  <span className="font-medium">{a.name}</span>
                  <span className="text-muted-foreground text-xs">{a.email}</span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter><Button onClick={save} className="rounded-lg" data-testid="save-agents-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function FundingRequests({ clientId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ amount: "", institution: "", purpose: "" });
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/clients/${clientId}/funding-requests`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);
  const add = async () => {
    const amt = parseFloat(form.amount);
    if (!amt) return toast.error("Enter an amount");
    try {
      await api.post(`/clients/${clientId}/funding-requests`, { amount: amt, institution: form.institution, purpose: form.purpose });
      toast.success("Funding request logged");
      setOpen(false); setForm({ amount: "", institution: "", purpose: "" }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.put(`/funding-requests/${id}/status`, { status }); load(); };
  const remove = async (id) => { await api.delete(`/funding-requests/${id}`); load(); };
  const STAT = { submitted: "bg-slate-500/20 text-slate-300", approved: "bg-blue-500/20 text-blue-300", funded: "bg-emerald-500/20 text-emerald-300", denied: "bg-red-500/20 text-red-300" };
  return (
    <Card className="p-6 bg-card/60 border-border mt-4" data-testid="funding-requests-card">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-display font-semibold">Funding Requests</h4>
        <Button size="sm" className="rounded-lg" onClick={() => setOpen(true)} data-testid="add-funding-btn"><Plus className="h-3.5 w-3.5 mr-1" /> New Request</Button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="rounded-lg border border-border p-3 bg-background/40 flex items-center justify-between gap-3" data-testid={`funding-${r.id}`}>
            <div className="min-w-0">
              <div className="font-mono font-semibold">${Number(r.amount).toLocaleString()}</div>
              <div className="text-xs text-muted-foreground truncate">{r.institution || "—"}{r.purpose ? ` · ${r.purpose}` : ""}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="bg-secondary border border-border rounded-md px-2 py-1 text-xs" data-testid={`funding-status-${r.id}`}>
                <option value="submitted">Submitted</option><option value="approved">Approved</option><option value="funded">Funded</option><option value="denied">Denied</option>
              </select>
              <Badge className={STAT[r.status]}>{r.status}</Badge>
              <Button size="icon" variant="ghost" className="text-destructive h-8 w-8" onClick={() => remove(r.id)} data-testid={`funding-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
        {!rows.length && <p className="text-sm text-muted-foreground">No funding requests yet.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border">
          <DialogHeader><DialogTitle className="font-display">New Funding Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Amount ($)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="funding-amount" /></div>
            <div className="space-y-1"><Label>Institution</Label><Input value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} data-testid="funding-institution" /></div>
            <div className="space-y-1"><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="funding-purpose" /></div>
          </div>
          <DialogFooter><Button onClick={add} className="rounded-lg" data-testid="save-funding-btn">Log Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function QualifyTab({ client }) {
  const [data, setData] = useState(null);
  const [advice, setAdvice] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  const [emailingCert, setEmailingCert] = useState(false);

  useEffect(() => { api.get(`/clients/${client.id}/qualifications`).then((r) => setData(r.data)).catch(() => {}); }, [client.id]);

  const getAi = async () => {
    setLoadingAi(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/qualifications/ai`);
      const result = await pollJob(data.job_id);
      setAdvice(result.advice);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); } finally { setLoadingAi(false); }
  };

  const emailCert = async () => {
    setEmailingCert(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/private-banking/certificate/email`);
      toast.success(`Approval emailed to ${data.sent_to}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not send email"); } finally { setEmailingCert(false); }
  };

  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-primary/10 to-transparent border-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-bold">Credit Standing</p>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="font-mono text-5xl font-bold">{data.credit_score || "—"}</span>
            <Badge className="bg-primary/20 text-primary">{data.tier_label}</Badge>
          </div>
        </div>
        <Button onClick={getAi} disabled={loadingAi} className="rounded-lg" data-testid="ai-advice-btn">
          {loadingAi ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />} AI Recommendation
        </Button>
      </Card>

      {data.private_banking && (
        <Card className={`p-6 border-border ${data.private_banking.approved ? "bg-gradient-to-br from-emerald-500/10 to-transparent" : "bg-card/30 opacity-80"}`} data-testid="private-banking-card">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-display font-semibold flex items-center gap-2"><Banknote className="h-5 w-5 text-primary" /> Private Banking Funding</h4>
            {data.private_banking.approved
              ? <Badge className="bg-emerald-500/20 text-emerald-300">Approved</Badge>
              : <Badge className="bg-secondary text-muted-foreground">Needs 580+</Badge>}
          </div>
          {data.private_banking.approved ? (
            <>
              <div className="text-3xl font-bold font-mono">${data.private_banking.min_amount.toLocaleString()} – ${data.private_banking.max_amount.toLocaleString()}</div>
              <p className="text-sm text-muted-foreground mt-1">Approved funding range for this client's <b>{data.private_banking.tier_label}</b> tier. See the Private Banking page for full instructions and partner institutions.</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button
                  size="sm"
                  className="rounded-lg"
                  onClick={() => window.open(`${API}/clients/${client.id}/private-banking/certificate?auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}`, "_blank")}
                  data-testid="download-certificate-btn"
                >
                  <FileText className="h-4 w-4 mr-2" /> Download Approval Certificate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-lg"
                  disabled={emailingCert}
                  onClick={emailCert}
                  data-testid="email-certificate-btn"
                >
                  {emailingCert ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Email to Client
                </Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">This client needs a credit score of <b>580+</b> to unlock private banking funding ($10,000 – $500,000,000). Keep disputing to raise the score.</p>
          )}
        </Card>
      )}

      <FundingRequests clientId={client.id} />
      {advice && (
        <Card className="p-6 bg-card/60 border-border" data-testid="ai-advice-content">
          <h4 className="font-display font-semibold mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Personalized Advisor</h4>
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-muted-foreground">{advice}</pre>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.categories.map((cat) => {
          const Icon = ICONS[cat.icon] || CreditCard;
          return (
            <Card key={cat.key} className={`p-5 border-border transition-transform hover:-translate-y-1 ${cat.qualified ? "bg-card/60" : "bg-card/30 opacity-70"}`} data-testid={`qualify-${cat.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center"><Icon className="h-5 w-5 text-primary" /></div>
                {cat.qualified ? <span className="flex items-center gap-1 text-xs text-emerald-400 font-medium"><Check className="h-3.5 w-3.5" /> Qualified</span> : <span className="flex items-center gap-1 text-xs text-muted-foreground"><X className="h-3.5 w-3.5" /> Not yet</span>}
              </div>
              <div className="font-display font-semibold">{cat.label}</div>
              <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{cat.detail}</div>
              <div className="mt-3 pt-3 border-t border-border text-sm"><span className="text-muted-foreground text-xs">Est. power: </span><span className="font-semibold">{cat.estimate}</span></div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
